/**
 * F5 (No VERI*FACTU) · el aviso de alarma de integridad, en todas las pantallas.
 *
 * La API (F2, `GET /api/companies/me/verifactu/alarmas`) abre una alarma cuando un registro
 * no se ha podido firmar o el certificado no es válido, y la mantiene abierta hasta que el
 * sistema la cierra (certificado renovado) o una persona de VERIX la revisa. Mientras está
 * abierta, el aviso se ve en todas las pantallas y para todos los usuarios de la empresa:
 * la ruta no exige rol (F2 CP14).
 *
 * Las reglas (D-A3 CORR, decisión del 15-10):
 *  1. 🔴 **No se puede cerrar.** No hay botón: lo que lo quita es que la alarma se cierre.
 *  2. Una alarma activa se sigue pintando aunque la consulta siguiente falle (react-query
 *     conserva el último dato). Perder la red no apaga el aviso.
 *  3. Sin dato y con error —un 500, un 404, la red— el aviso es «no hemos podido
 *     comprobar», NUNCA «sin alarmas»: no saber no es lo mismo que no haber.
 *  4. Todo error se reporta.
 *
 * **Solo en `local_signed`** (D-F5-3 (b), 16-10): F2 solo abre alarmas en empresas No
 * VERI*FACTU y no hay paso de No VERI*FACTU a VERI*FACTU. Consultar desde una empresa
 * VERI*FACTU sería tráfico para nada y, si fallara, un aviso sobre algo que no le aplica.
 * Si F2 llegara a abrir alarmas en otro modo, hay que tocar `enabled` aquí.
 *
 * El texto no dice «VERIX está avisado» ni «hemos abierto una alarma»: no todo rechazo
 * abre alarma, y el aviso habla solo de lo que ve.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Info } from 'lucide-react';
import { apiFetch, unwrapOne } from '@/utils/api';
import { useCurrentCompany } from '@/components/hooks/useApp';
import { captureLog } from '@/lib/errorReporter';
import { createPageUrl } from '@/components/utils';
import { qk } from '@/lib/qk';

const __LOG_CTX = { context: 'src/components/layout/VerifactuAlarmaBanner' };

/** La consulta de `/alarmas`. La comparten el aviso del layout y la tarjeta de la pantalla. */
export function useVerifactuAlarmas() {
  const { data: company } = useCurrentCompany();
  const query = useQuery({
    queryKey: qk.fiscal.verifactuAlarmas(company?.id),
    queryFn: async () => unwrapOne(await apiFetch('/api/companies/me/verifactu/alarmas')),
    enabled: !!company?.id && company?.verifactu_mode === 'local_signed',
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchOnWindowFocus: true,
    retry: 2,
  });
  return { ...query, companyId: company?.id };
}

export default function VerifactuAlarmaBanner() {
  const { t } = useTranslation();
  const { data, isError, error, companyId } = useVerifactuAlarmas();

  useEffect(() => {
    if (!error) return;
    captureLog('error', __LOG_CTX, 'no se pudo consultar /verifactu/alarmas', {
      company_id: companyId, status: error?.status ?? null, code: error?.code ?? null,
    });
  }, [error, companyId]);

  if (data?.activa === true) {
    return (
      <div
        role="alert"
        data-testid="verifactu-alarma-banner"
        className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2 text-sm bg-destructive/10 text-destructive border-destructive/40"
      >
        <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="font-medium">{t('verifactu_alarma.activa_titulo')}</span>
        <span>{t('verifactu_alarma.activa_texto')}</span>
        <Link to={createPageUrl('VeriFactu')} className="underline underline-offset-2 font-medium">
          {t('verifactu_alarma.ver_detalle')}
        </Link>
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div
        role="status"
        data-testid="verifactu-alarma-sin-dato"
        className="flex items-center gap-2 border-b px-4 py-2 text-sm bg-muted text-muted-foreground"
      >
        <Info className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span>{t('verifactu_alarma.sin_dato')}</span>
      </div>
    );
  }

  return null;
}
